import React, {
  ChangeEvent,
  isValidElement,
  ReactNode,
  MouseEvent,
} from 'react';
import clsx from 'clsx';
import MuiCheckbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ToolTip from 'core/components/Tooltip';

type ComponentParts = 'root' | 'label' | 'checkbox';

type Placement = 'end' | 'top' | 'bottom' | 'start';

type Text = string | ReactNode;

type LabelProps = {
  text?: Text;
  placement?: Placement;
};

type Props = {
  className?: string;
  classes?: Partial<Record<ComponentParts, string>>;
  containerClassName?: string;
  id: string;
  label?: LabelProps | Text;
  value?: string;
  color?: 'primary' | 'secondary';
  onChange: (checked: boolean, event?: ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly?: boolean;
  disabled?: boolean;
  checked: boolean;
  indeterminate?: boolean;
  readonlyMessage?: string;
};

export default function Checkbox({
  className,
  classes,
  containerClassName,
  id,
  label,
  color,
  value,
  onChange,
  checked,
  disabled,
  readonly,
  readonlyMessage,
  indeterminate = false,
  onClick,
}: Props) {
  const isObject = !(isValidElement(label) || typeof label === 'string');
  const labelPlacement = isObject ? (label as LabelProps)?.placement : 'end';
  const labelText = isObject ? (label as LabelProps)?.text : label;
  return (
    <ToolTip
      title={readonlyMessage}
      hide={!readonly}
    >
      <div className={clsx('flex items-center', containerClassName, classes?.root)}>
        <FormControlLabel
          className={clsx('!contents', classes?.label)}
          labelPlacement={labelPlacement}
          classes={{ label: classes?.label }}
          control={(
            <MuiCheckbox
              className={clsx(
                'w-2 h-2',
                { 'opacity-70': readonly },
                className,
                classes?.checkbox,
              )}
              disabled={disabled || readonly}
              checked={checked}
              color={color}
              onChange={(event, checked: boolean) => { onChange(checked, event); }}
              id={id}
              value={value}
              size="small"
              indeterminate={indeterminate}
              onClick={onClick}
            />
          )}
          label={labelText}
        />
      </div>
    </ToolTip>
  );
}
